import type { AlertLevel, DroneTelemetry } from '@shared/index';

/**
 * 总览/列表里展示的机队状态。
 *
 * 无人机本身只上报飞行状态，没有「在线/离线」这种平台侧概念，
 * 所以这里由「是否收到新鲜遥测帧」+「告警等级」推导出来，保证同一个状态
 * 在总览卡片、列表行、单机管理页上的判定完全一致。
 */
export type FleetStatus = 'flying' | 'idle' | 'offline' | 'fault' | 'disabled';

export const FLEET_STATUS_LABELS: Readonly<Record<FleetStatus, string>> = {
  flying: '飞行中',
  idle: '待命',
  offline: '离线',
  fault: '故障',
  disabled: '已停用',
};

export const FLEET_STATUS_DESCRIPTIONS: Readonly<Record<FleetStatus, string>> = {
  flying: '已解锁且在空中',
  idle: '在线且在地面待命',
  offline: '超过失联阈值未收到遥测帧',
  fault: '存在严重告警项',
  disabled: '平台侧已停用，不参与监管',
};

export interface FleetStatusInput {
  /** 平台侧是否启用该机 */
  enabled: boolean;
  /** 是否已收到过遥测 */
  hasTelemetry: boolean;
  /** 最近一帧距今的毫秒数 */
  ageMs: number | null;
  /** 失联阈值 */
  staleAfterMs: number;
  /** 最近一次告警评估的最严重等级 */
  alertLevel: AlertLevel;
  /** 是否在空中 */
  inAir: boolean;
}

export function fleetStatusOf(input: FleetStatusInput): FleetStatus {
  if (!input.enabled) return 'disabled';
  if (!input.hasTelemetry || input.ageMs === null || input.ageMs > input.staleAfterMs) return 'offline';
  if (input.alertLevel === 'critical') return 'fault';
  if (input.inAir) return 'flying';
  return 'idle';
}

/** 链路质量（0-100）分档，用于卡片与列表的配色。 */
export function linkQualityTone(quality: number): 'ok' | 'caution' | 'warning' | 'critical' {
  if (quality >= 75) return 'ok';
  if (quality >= 50) return 'caution';
  if (quality >= 25) return 'warning';
  return 'critical';
}

export function batteryTone(percent: number): 'ok' | 'caution' | 'warning' | 'critical' {
  if (percent > 50) return 'ok';
  if (percent > 30) return 'caution';
  if (percent > 15) return 'warning';
  return 'critical';
}

/** 同一架机的遥测帧是否已经过期（用于列表里给旧数据打灰）。 */
export function isStale(telemetry: DroneTelemetry | undefined, now: number, staleAfterMs: number): boolean {
  if (!telemetry) return true;
  return now - telemetry.receivedAt > staleAfterMs;
}
